/**
 * «Escribir con IA» en Mensajes (Alan, 01/11/2026: la IA escribe y el operador aprueba), lógica pura de la pantalla.
 *
 * - **Mail:** lo que escribió la IA pasa a ser el asunto y el texto del grupo, que se siguen editando como siempre. El
 *   texto que había queda para «Recuperar mi texto» (un paso), igual que cuando se rearma desde el catálogo.
 * - **WhatsApp oficial:** la propuesta de plantilla queda en el grupo (`propuestaIa`) y se guarda con el borrador. El
 *   operador edita el cuerpo y los ejemplos; la vista previa le pasa las reglas de Meta y las de Mensajes en vivo.
 * - Nada de esto manda nada: el mail sale por Snov y la plantilla tiene que crearse y aprobarse en Meta.
 */

import type { Borrador } from "./mensajes";
import type { PropuestaIa, RespuestaIa } from "../types";

export const INDICACIONES_MAX = 300;

/** Lo que lee el operador si el backend no mandó su texto (el backend siempre manda uno: es el respaldo). */
const TEXTO_POR_CODIGO: Record<string, string> = {
  IA_NO_CONFIGURADA: "La IA de Mensajes no está prendida. Seguí con «Otra versión» o escribí el texto a mano.",
  IA_OCUPADA: "La IA está con muchos pedidos en este momento. Esperá un minuto y probá de nuevo.",
  IA_NO_RESPONDE: "La IA no respondió. Probá de nuevo en un rato.",
  IA_RECHAZO: "La IA no quiso escribir este mensaje. Cambiá las indicaciones y probá de nuevo, o escribilo a mano.",
  IA_INCOMPLETA: "La IA no llegó a terminar el mensaje. Probá de nuevo.",
  IA_ERROR: "La IA tuvo un problema y no escribió nada. Probá de nuevo.",
  IA_TOPE: "Se pidieron muchos mensajes a la IA en la última hora. Esperá un rato y probá de nuevo.",
};

/** El texto de un error de «Escribir con IA»: el del backend; si no vino, el del código; si no, el genérico. */
export function textoErrorIa(f: { code: string; error: string }, generico: string): string {
  return f.error.trim() || TEXTO_POR_CODIGO[f.code] || generico;
}

/** El mail de la IA en el grupo: el texto que había queda para «Recuperar mi texto». */
export function aplicarMail(b: Borrador, r: Extract<RespuestaIa, { canal: "email" }>): Borrador {
  const cambia = r.asunto !== b.asunto || r.texto !== b.texto;
  return { ...b, asunto: r.asunto, texto: r.texto, editado: true, respaldo: cambia ? { asunto: b.asunto, texto: b.texto, editado: b.editado } : b.respaldo };
}

export const aplicarPropuesta = (b: Borrador, p: PropuestaIa): Borrador => ({ ...b, propuestaIa: p });

export const descartarPropuesta = (b: Borrador): Borrador => ({ ...b, propuestaIa: null });

export function cambiarCuerpo(b: Borrador, cuerpo: string): Borrador {
  return b.propuestaIa ? { ...b, propuestaIa: { ...b.propuestaIa, cuerpo } } : b;
}

export function cambiarEjemplo(b: Borrador, numero: number, ejemplo: string): Borrador {
  if (!b.propuestaIa) return b;
  return { ...b, propuestaIa: { ...b.propuestaIa, variables: b.propuestaIa.variables.map((v) => (v.numero === numero ? { ...v, ejemplo } : v)) } };
}

/** Las variables que el cuerpo usa, en orden de aparición y sin repetir. */
export function variablesDelCuerpo(cuerpo: string): number[] {
  const out: number[] = [];
  for (const m of cuerpo.matchAll(/\{\{([1-9]\d*)\}\}/g)) {
    const n = Number(m[1]);
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

/** Cómo le llega con los ejemplos: el cuerpo y el pie, con cada {{n}} reemplazada por su ejemplo (una sin ejemplo queda). */
export function textoConEjemplos(p: PropuestaIa): string {
  const ejemplos = new Map(p.variables.map((v) => [String(v.numero), v.ejemplo]));
  const cuerpo = p.cuerpo.trimEnd();
  const todo = p.pie.trim() ? `${cuerpo}\n\n${p.pie.trim()}` : cuerpo;
  return todo.replace(/\{\{([1-9]\d*)\}\}/g, (m, n: string) => (ejemplos.get(n)?.trim() ? (ejemplos.get(n) as string) : m));
}

/** El nombre de cada dato, para mostrar al lado de su variable. */
export const NOMBRE_DEL_DATO: Record<string, string> = {
  primerNombre: "Primer nombre",
  sucursal: "Sucursal",
  marca: "Marca",
  oferta: "Oferta de su grupo",
  cupon: "Código de cupón",
  vence: "Vencimiento del beneficio",
};
